// blogGeneration.ts
//
// Shared blog-generation helpers + contract for the external `cron_job` repo.
// Mode / pending best-post live on `template_settings.tweaks.blogGen`.

import { supabase } from '../../config/supabaseClient';
import { saveBlogDraft } from './BlogDatabase';
import { loadTemplateSettings } from './TemplateDatabase';
import type { BlogGenerationVersion } from './blogArchetype';

export const BLOG_ELIGIBLE_TYPES = ['REELS', 'VIDEO', 'PODCAST'] as const;
export type BlogEligibleType = (typeof BLOG_ELIGIBLE_TYPES)[number];

/**
 * Content-bank "Generate" eligibility — same gate as FeedActionButtons.
 * REELS / VIDEO / PODCAST, podcast platform, PRODUCT rows, or any shop_link.
 */
export function isContentBankBlogEligible(input: {
  type?: unknown;
  platform?: unknown;
  shop_link?: unknown;
}): boolean {
  const t = String(input.type || '').toUpperCase();
  if (t === 'REELS' || t === 'VIDEO' || t === 'PODCAST') return true;
  if (String(input.platform || '').toLowerCase() === 'podcast') return true;
  if (t === 'PRODUCT') return true;
  if (input.shop_link && String(input.shop_link).trim()) return true;
  return false;
}

/**
 * `user.blog_feature` (Engineering Controls). Admin login always sees Blog UI.
 */
export function isBlogFeatureEnabled(
  userData?: { blog_feature?: unknown } | null,
  isAdmin?: boolean,
): boolean {
  if (isAdmin) return true;
  return Boolean(userData?.blog_feature);
}

export type BlogGenMode = 'weekly_roundup' | 'ai_best_post';

/** Cron / propose writes this; tweaks shows title + Generate. */
export interface BlogPendingBestPost {
  source_post_id: string;
  title?: string | null;
  rationale?: string | null;
  caption?: string | null;
  type?: string | null;
  platform?: string | null;
  url?: string | null;
  media_url?: string | null;
  suggested_at?: string | null;
  generated?: boolean;
  generatedBlogId?: number | string | null;
}

export interface BlogPendingWeeklyRoundup {
  title: string;
  topic?: string | null;
  post_ids: string[];
  proposal_id?: string | null;
  proposed_at?: string | null;
  generated?: boolean;
  generatedBlogId?: number | string | null;
}

/**
 * Stored under `tweaks.blogGen` (read by frontend + cron_job).
 *
 * Flow:
 * - Creator toggles weeklyRoundup and/or aiBestPost
 * - autoPublish: when true, cron_job publishes generated posts as active (no review queue)
 * - "Generate now" (or scheduled cron) calls step=generate_now
 */
export interface BlogGenSettings {
  weeklyRoundup: boolean;
  aiBestPost: boolean;
  /** When true, cron publishes generated posts without manual review. */
  autoPublish?: boolean;
  /**
   * Website bootstrap pipeline. `"v1"` = existing (default). `"v2"` = archetype
   * affiliate / youtuber generate. Opt-in only — does not change current builds.
   */
  version?: 'v1' | 'v2';
  /** @deprecated Migrated into weeklyRoundup / aiBestPost. */
  mode?: BlogGenMode;
  pendingBestPost?: BlogPendingBestPost | null;
  pendingWeeklyRoundup?: BlogPendingWeeklyRoundup | null;
  /** ISO timestamp of last generate/cron pass (watermark). */
  lastRoundupAt?: string | null;
}

export const DEFAULT_BLOG_GEN: BlogGenSettings = {
  weeklyRoundup: false,
  aiBestPost: false,
  /** Default: generated posts wait in Needs review until the creator publishes. */
  autoPublish: false,
  version: 'v1',
  pendingBestPost: null,
  pendingWeeklyRoundup: null,
  lastRoundupAt: null,
};

function parsePendingWeekly(raw: unknown): BlogPendingWeeklyRoundup | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const title = String(o.title ?? '').trim();
  if (!title) return null;
  const post_ids = Array.isArray(o.post_ids)
    ? o.post_ids.map((id) => String(id)).filter(Boolean)
    : [];
  return {
    title,
    topic: o.topic != null ? String(o.topic) : title,
    post_ids,
    proposal_id: o.proposal_id != null ? String(o.proposal_id) : null,
    proposed_at: o.proposed_at != null ? String(o.proposed_at) : null,
    generated: Boolean(o.generated),
    generatedBlogId:
      o.generatedBlogId != null ? (o.generatedBlogId as number | string) : null,
  };
}

function parsePendingBest(raw: unknown): BlogPendingBestPost | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const source_post_id = String(p.source_post_id ?? '').trim();
  if (!source_post_id) return null;
  return {
    source_post_id,
    title: p.title != null ? String(p.title) : null,
    rationale: p.rationale != null ? String(p.rationale) : null,
    caption: p.caption != null ? String(p.caption) : null,
    type: p.type != null ? String(p.type) : null,
    platform: p.platform != null ? String(p.platform) : null,
    url: p.url != null ? String(p.url) : null,
    media_url: p.media_url != null ? String(p.media_url) : null,
    suggested_at: p.suggested_at != null ? String(p.suggested_at) : null,
    generated: Boolean(p.generated),
    generatedBlogId:
      p.generatedBlogId != null ? (p.generatedBlogId as number | string) : null,
  };
}

export function normalizeBlogGen(raw: unknown): BlogGenSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BLOG_GEN };
  const o = raw as Record<string, unknown>;

  const legacyMode =
    o.mode === 'weekly_roundup' || o.mode === 'ai_best_post' ? o.mode : null;
  const hasWeeklyFlag = typeof o.weeklyRoundup === 'boolean';
  const hasAiFlag = typeof o.aiBestPost === 'boolean';

  let weeklyRoundup = hasWeeklyFlag ? Boolean(o.weeklyRoundup) : false;
  let aiBestPost = hasAiFlag ? Boolean(o.aiBestPost) : false;
  if (!hasWeeklyFlag && !hasAiFlag && legacyMode) {
    weeklyRoundup = legacyMode === 'weekly_roundup';
    aiBestPost = legacyMode === 'ai_best_post';
  }


  return {
    weeklyRoundup,
    aiBestPost,
    autoPublish: typeof o.autoPublish === 'boolean' ? o.autoPublish : false,
    version: o.version === 'v2' ? 'v2' : 'v1',
    pendingBestPost: parsePendingBest(o.pendingBestPost),
    pendingWeeklyRoundup: parsePendingWeekly(o.pendingWeeklyRoundup),
    lastRoundupAt:
      o.lastRoundupAt != null && String(o.lastRoundupAt).trim()
        ? String(o.lastRoundupAt)
        : null,
  };
}

export function isBlogGenEnabled(settings: BlogGenSettings): boolean {
  return Boolean(settings.weeklyRoundup || settings.aiBestPost);
}

/** Read `tweaks.blogGen.version` — default `"v1"` so existing builds stay unchanged. */
export async function loadBlogGenVersion(
  userId?: string | null,
): Promise<BlogGenerationVersion> {
  const uid = String(userId || '').trim();
  if (!uid) return 'v1';
  try {
    const row = await loadTemplateSettings(uid);
    const tweaks = (row?.tweaks || {}) as Record<string, unknown>;
    return normalizeBlogGen(tweaks.blogGen).version === 'v2' ? 'v2' : 'v1';
  } catch {
    return 'v1';
  }
}

const API_BASE = 'https://dev.thesearchbubble.com';
// Production: 'https://dev.thesearchbubble.com'

export type BlogGenAction = 'weekly_roundup' | 'ai_best_post' | 'both';
export type BlogGenStep = 'propose' | 'generate' | 'generate_now';

export interface BlogGenCronResultItem {
  action?: BlogGenAction | string;
  title?: string;
  blogId?: number | string;
  source_post_id?: string;
  rationale?: string;
  error?: string;
}

export interface BlogGenCronResponse {
  ok: boolean;
  step?: BlogGenStep;
  action?: BlogGenAction;
  title?: string;
  blogId?: number | string;
  generated?: boolean;
  alreadyGenerated?: boolean;
  error?: string;
  proposal?: BlogPendingBestPost | BlogPendingWeeklyRoundup | null;
  source_post_id?: string;
  rationale?: string;
  results?: BlogGenCronResultItem[];
  summary?: {
    generated?: number;
    weekly?: number;
    aiBest?: number;
    errors?: number;
  };
}

/** One-shot generate + store via cron_job /api/blog/gen-cron. */
export async function callBlogGenCron(params: {
  username: string;
  userId?: string;
  action?: BlogGenAction;
  step?: BlogGenStep;
  weeklyRoundup?: boolean;
  aiBestPost?: boolean;
  autoPublish?: boolean;
  /** Opt-in archetype pipeline; cron may ignore until prompts land. */
  blogVersion?: BlogGenerationVersion;
}): Promise<BlogGenCronResponse> {
  const username = params.username?.trim().toLowerCase();
  if (!username) return { ok: false, error: 'Missing username.' };

  try {
    const res = await fetch(`${API_BASE}/api/blog/gen-cron`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username,
        user_id: params.userId,
        action: params.action,
        step: params.step || 'generate_now',
        generateNow: true,
        weeklyRoundup: params.weeklyRoundup,
        aiBestPost: params.aiBestPost,
        // Always send an explicit boolean — omit/undefined was treated as
        // auto-publish on the cron side for some deployments.
        autoPublish: params.autoPublish === true,
        ...(params.blogVersion === 'v2' ? { blogVersion: 'v2' } : {}),
      }),
    });
    const data = (await res.json().catch(() => ({}))) as BlogGenCronResponse;
    const firstResultError = Array.isArray(data?.results)
      ? data.results.find((r) => r?.error)?.error
      : undefined;
    // Cron often returns HTTP 400 with ok:false and only results[].error set.
    if (!res.ok || data?.ok === false) {
      return {
        ...data,
        ok: false,
        error:
          data?.error ||
          firstResultError ||
          `Request failed (${res.status})`,
        results: data?.results,
        summary: data?.summary,
      };
    }
    return { ...data, ok: data?.ok !== false };
  } catch (e) {
    console.warn('[blogGeneration] callBlogGenCron failed', e);
    return { ok: false, error: 'Failed to reach blog generation API.' };
  }
}

/** Target corpus size; split evenly across eligible types (~7–8 each). */
export const VOICE_SAMPLE_TARGET = 24;

type ContentRow = {
  post_id?: string | number;
  caption?: string | null;
  content_title?: string | null;
  platform?: string | null;
  type?: string | null;
  url?: string | null;
};

/**
 * Fetch ~20–25 captions equally divided across REELS / VIDEO / PODCAST
 * for creator-voice blog generation.
 *
 * Prefers the creator `{username}` table (has `content_title` / captions).
 * Falls back to `content_instagram` captions when username is unavailable.
 */
export async function fetchCreatorVoiceSamples(
  userId: string,
  targetTotal = VOICE_SAMPLE_TARGET,
  username?: string,
): Promise<string[]> {
  const uid = userId?.trim();
  const uname = username?.trim().toLowerCase();
  if (!uid && !uname) return [];

  const perType = Math.max(1, Math.ceil(targetTotal / BLOG_ELIGIBLE_TYPES.length));

  const queues = await Promise.all(
    BLOG_ELIGIBLE_TYPES.map(async (typeVal) => {
      if (uname) {
        const { data, error } = await supabase
          .from(uname)
          .select('post_id,caption,content_title,type')
          .eq('type', typeVal)
          .order('post_date', { ascending: false })
          .limit(perType);

        if (error || !Array.isArray(data)) {
          console.warn(
            `[blogGeneration] voice sample ${typeVal} (${uname}):`,
            error?.message,
          );
          return [] as ContentRow[];
        }
        return data as ContentRow[];
      }

      // content_instagram has caption/type only — no content_title column.
      const { data, error } = await supabase
        .from('content_instagram')
        .select('post_id,caption,type')
        .eq('user_id', uid!)
        .eq('type', typeVal)
        .order('post_date', { ascending: false })
        .limit(perType);

      if (error || !Array.isArray(data)) {
        console.warn(`[blogGeneration] voice sample ${typeVal}:`, error?.message);
        return [] as ContentRow[];
      }
      return data as ContentRow[];
    }),
  );

  const captions: string[] = [];
  for (const row of queues.flat()) {
    const cap = typeof row.caption === 'string' ? row.caption.trim() : '';
    const title =
      typeof row.content_title === 'string' ? row.content_title.trim() : '';
    const text = cap || title;
    if (text) captions.push(text.slice(0, 800));
  }
  return captions.slice(0, targetTotal);
}

export interface GenerateBlogFromPostInput {
  username: string;
  userId: string;
  topic: string;
  post: Record<string, unknown>;
  sourcePostId: string;
  platform?: unknown;
  mode?: BlogGenMode | 'single';
  /** When true, fetch and attach voice samples (default true). */
  includeVoice?: boolean;
  /** Pre-fetched voice captions — skips a repeat DB fetch when batching. */
  voiceSamples?: string[];
}

export interface GenerateBlogFromPostResult {
  title: string;
  body: string;
  coverImage?: string;
  coverPostId?: string;
  product_ids?: string[];
  error?: string;
}

/** Dashboard New post. Uses `/api/blog/generate-from-prompt`, not production `/api/blog/generate`. */
export async function generateBlogFromUserPrompt(params: {
  username: string;
  userPrompt: string;
}): Promise<GenerateBlogFromPostResult> {
  const uname = params.username?.trim().toLowerCase();
  const userPrompt = params.userPrompt?.trim();
  if (!uname || !userPrompt) {
    return { title: '', body: '', error: 'Missing username or prompt.' };
  }

  try {
    const res = await fetch(`${API_BASE}/api/blog/generate-from-prompt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: uname, userPrompt }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        title: '',
        body: '',
        error: (data as { error?: string })?.error || `Generate failed (${res.status})`,
      };
    }
    return {
      title: typeof (data as { title?: string }).title === 'string'
        ? (data as { title: string }).title
        : 'Untitled',
      body: typeof (data as { body?: string }).body === 'string'
        ? (data as { body: string }).body
        : '',
    };
  } catch (e) {
    console.warn('[blogGeneration] generate-from-prompt failed', e);
    return { title: '', body: '', error: 'Failed to generate blog.' };
  }
}

/** Call `/api/blog/generate` with optional voice corpus (transcript handled server-side). */
export async function generateBlogFromPost(
  input: GenerateBlogFromPostInput,
): Promise<GenerateBlogFromPostResult> {
  const uname = input.username?.trim().toLowerCase();
  const topic = input.topic?.trim();
  if (!uname || !topic) {
    return { title: '', body: '', error: 'Missing username or topic.' };
  }

  let voiceSamples: string[] = Array.isArray(input.voiceSamples)
    ? input.voiceSamples
    : [];
  if (
    voiceSamples.length === 0 &&
    input.includeVoice !== false &&
    (input.userId || uname)
  ) {
    try {
      voiceSamples = await fetchCreatorVoiceSamples(
        input.userId,
        VOICE_SAMPLE_TARGET,
        uname,
      );
    } catch (e) {
      console.warn('[blogGeneration] voice sample failed', e);
    }
  }

  try {
    const res = await fetch(`${API_BASE}/api/blog/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: uname,
        topic,
        post: {
          ...input.post,
          post_id: input.sourcePostId,
          user_id: input.userId,
        },
        voiceSamples,
        generationMode: input.mode || 'single',
        // Server: if post has transcript use it + caption; else generate transcript first.
        useTranscript: true,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        title: '',
        body: '',
        error: (data as { error?: string })?.error || `Generate failed (${res.status})`,
      };
    }
    return {
      title: typeof (data as { title?: string }).title === 'string'
        ? (data as { title: string }).title
        : 'Untitled',
      body: typeof (data as { body?: string }).body === 'string'
        ? (data as { body: string }).body
        : '',
    };
  } catch (e) {
    console.warn('[blogGeneration] generate failed', e);
    return { title: '', body: '', error: 'Failed to generate blog.' };
  }
}

/** Generate from a pending best-post suggestion and save as draft. */
export async function generateAndSaveFromPendingBestPost(
  username: string,
  userId: string,
  pending: BlogPendingBestPost,
): Promise<{ ok: boolean; error?: string }> {
  const sourcePostId = pending.source_post_id?.trim();
  if (!sourcePostId) return { ok: false, error: 'No suggested post.' };

  const topic =
    (pending.caption || pending.title || `Post ${sourcePostId}`).slice(0, 500);

  const post = {
    post_id: sourcePostId,
    caption: pending.caption,
    content_title: pending.title,
    type: pending.type,
    platform: pending.platform,
    url: pending.url,
    media_url: pending.media_url,
    user_id: userId,
  };

  const gen = await generateBlogFromPost({
    username,
    userId,
    topic,
    post,
    sourcePostId,
    platform: pending.platform,
    mode: 'ai_best_post',
  });

  if (gen.error) return { ok: false, error: gen.error };

  const { error } = await saveBlogDraft(
    username,
    userId,
    topic,
    gen.title,
    gen.body,
    sourcePostId,
    pending.platform,
    post,
  );

  if (error) return { ok: false, error: error.message || 'Failed to save draft.' };
  return { ok: true };
}
